/**
 * F-174: the text of a block kept in this browser when its save failed
 * (`useBlockLock`). A shared computer must never show it to the next person:
 * the key carries the community and the member, the draft carries its owner
 * (a mismatch is dropped), it expires after 7 days, and every draft goes when
 * the session ends or the acting member changes (`clearBlockDrafts`).
 *
 * Pure TypeScript (no React, no `@/` imports).
 */

export const DRAFT_PREFIX = 'memberfy:blockDraft:'
export const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000

export interface StoredBlockDraft {
  value: unknown
  at: string
  communityId: string
  memberId: string
}

export interface DraftOwner {
  communityId: string | null | undefined
  memberId: string | null | undefined
}

const storage = (): Storage | null => {
  try {
    return typeof window !== 'undefined' ? window.localStorage : (globalThis as { localStorage?: Storage }).localStorage || null
  } catch {
    return null
  }
}

/** The storage key of a piece's draft, or null without a known owner. */
export const draftKey = (owner: DraftOwner, lockId: string | null): string | null =>
  (owner.communityId && owner.memberId && lockId ? `${DRAFT_PREFIX}${owner.communityId}:${owner.memberId}:${lockId}` : null)

export const readBlockDraft = (owner: DraftOwner, lockId: string | null, now = Date.now()): StoredBlockDraft | null => {
  const key = draftKey(owner, lockId)
  const store = storage()
  if (!key || !store) return null
  try {
    const raw = store.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw) as StoredBlockDraft
    const at = parsed && typeof parsed.at === 'string' ? Date.parse(parsed.at) : NaN
    const valid = parsed && parsed.memberId === owner.memberId && parsed.communityId === owner.communityId
      && Number.isFinite(at) && now - at < DRAFT_TTL_MS
    if (!valid) {
      store.removeItem(key)
      return null
    }
    return parsed
  } catch {
    return null
  }
}

export const writeBlockDraft = (owner: DraftOwner, lockId: string | null, value: unknown | null, now = Date.now()): StoredBlockDraft | null => {
  const key = draftKey(owner, lockId)
  const store = storage()
  if (!key || !store) return null
  try {
    if (value === null) {
      store.removeItem(key)
      return null
    }
    const draft: StoredBlockDraft = { value, at: new Date(now).toISOString(), communityId: owner.communityId!, memberId: owner.memberId! }
    store.setItem(key, JSON.stringify(draft))
    return draft
  } catch {
    /* Private window or full storage: the notice still shows for this page. */
    return null
  }
}

/** Every kept draft goes (session ended, logout, another acting member). */
export const clearBlockDrafts = () => {
  const store = storage()
  if (!store) return
  try {
    const keys: string[] = []
    for (let i = 0; i < store.length; i += 1) {
      const key = store.key(i)
      if (key && key.startsWith(DRAFT_PREFIX)) keys.push(key)
    }
    for (const key of keys) store.removeItem(key)
  } catch {
    /* nothing to clear */
  }
}
